import React, { useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { AnalyticsDashboard } from './AnalyticsDashboard';
import { useScreenInit } from '../useScreenInit.js';
import { NoticeComposer } from './NoticeComposer';
import { NoticeList } from './NoticeList';
import { LegacyNoticeComposer } from './LegacyNoticeComposer';
import { CommunicationsConfigurer, MOCK_CATEGORIES, type Categorie, type ConfigSection } from './CommunicationsConfigurer';
import { useLanguage } from '../contexts/LanguageContext';
import { NOTICES } from '../data/notices';
import { getInitialTemplates, type NoticeTemplate } from '../data/communications';
import type { Notice } from '../types/notice';
type SubTab = 'gerer' | 'analytiques' | 'configurer';

/** Template editor state within "Configurer": closed, or open on a template. */
type TemplateEditor = {
  open: false;
} | {
  open: true;
  template: NoticeTemplate | null;
};
export function CreateNoticeForm() {
  const {
    t
  } = useLanguage();
  const screenInit = useScreenInit();
  const [activeSubTab, setActiveSubTab] = useState<SubTab>(screenInit.activeSubTab ?? 'gerer');
  /** Within "Gérer": browsing the notice list, or inside the composer. */
  const [gererView, setGererView] = useState<'list' | 'composer'>(screenInit.gererView ?? 'list');
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  /** Notice the analytics tab is filtered on, if any. */
  const [focusedNotice, setFocusedNotice] = useState<Notice | null>(null);
  const [notices] = useState<Notice[]>(NOTICES);
  /** Every template — built-in catalogue plus those saved by the team. */
  const [templates, setTemplates] = useState<NoticeTemplate[]>(getInitialTemplates);
  const [templateEditor, setTemplateEditor] = useState<TemplateEditor>({
    open: false
  });
  const [configSection, setConfigSection] = useState<ConfigSection>('categories');
  /** Lifted here so edits survive switching tabs or opening a template. */
  const [categories, setCategories] = useState<Categorie[]>(MOCK_CATEGORIES);
  const [contentLang, setContentLang] = useState<'fr' | 'en'>('fr');
  const [legacyMode, setLegacyMode] = useState(false);
  const tabs: {
    id: SubTab;
    label: string;
  }[] = [{
    id: 'gerer',
    label: t('Gérer', 'Manage')
  }, {
    id: 'analytiques',
    label: t('Analytiques', 'Analytics')
  }, {
    id: 'configurer',
    label: t('Configurer', 'Configure')
  }];
  const openComposer = (notice: Notice | null) => {
    setEditingNotice(notice);
    setGererView('composer');
  };
  const backToList = () => {
    setGererView('list');
    setEditingNotice(null);
  };
  const viewStats = (notice: Notice) => {
    setFocusedNotice(notice);
    setActiveSubTab('analytiques');
  };
  const selectTab = (id: SubTab) => {
    setActiveSubTab(id);
    setTemplateEditor({
      open: false
    });
    // Choosing the tab directly always shows the whole-module view.
    if (id === 'analytiques') setFocusedNotice(null);
  };

  /** Creates a template, or replaces an existing one when editing. */
  const saveTemplate = (tpl: NoticeTemplate, replacesId?: string) => setTemplates(prev => replacesId ? prev.map(existing => existing.id === replacesId ? tpl : existing) : [tpl, ...prev]);
  const openTemplateEditor = (template: NoticeTemplate | null) => setTemplateEditor({
    open: true,
    template
  });
  const closeTemplateEditor = () => {
    setTemplateEditor({
      open: false
    });
    setConfigSection('modeles');
  };
  const inComposer = activeSubTab === 'gerer' && gererView === 'composer';
  const inTemplateEditor = activeSubTab === 'configurer' && templateEditor.open;
  const heading = inComposer ? editingNotice ? t("Modifier l'avis", 'Edit notice') : t('Créer un avis', 'Create a notice') : inTemplateEditor ? templateEditor.template ? t('Modifier le modèle', 'Edit template') : t('Nouveau modèle', 'New template') : t('Communications', 'Communications');
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1801" data-mp="231jcmldwt1ij">
      {/* Page title & navigation */}
      <div className="mb-8" data-id="element-1802" data-mp="1y2q0oxe9tikh">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6" data-id="element-1803" data-mp="1y3hjt8qolntv">
          <div className="min-w-0" data-id="element-1804" data-mp="1taofun31ejiu">
            {(inComposer || inTemplateEditor) && <button onClick={inComposer ? backToList : closeTemplateEditor} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#4F46E5] transition-colors mb-1" data-id="element-1805" data-mp="1odesdcz7khxn">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-1806" data-mp="1fe1jalezxvdg" />
                {inComposer ? t('Retour aux avis', 'Back to notices') : t('Retour aux modèles', 'Back to templates')}
              </button>}
            <h1 className="text-2xl font-bold text-[#0F172A] font-heading truncate" data-id="element-1807" data-mp="34renman3zjek">
              {heading}
            </h1>
          </div>

          <nav aria-label={t('Sections du module', 'Module sections')} className="flex items-center gap-2" data-id="element-1808" data-mp="2zslbaljk5yfz">
            {tabs.map(tab => <button key={tab.id} onClick={() => selectTab(tab.id)} className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${activeSubTab === tab.id ? 'text-white bg-[#0F172A] shadow-sm hover:bg-[#1E293B]' : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-1809" data-mp="3c9356m0oqd9g">
                {tab.label}
              </button>)}
          </nav>
        </div>
      </div>

      {activeSubTab === 'analytiques' ? <AnalyticsDashboard focusedNotice={focusedNotice} onClearFocus={() => setFocusedNotice(null)} data-id="element-1810" data-mp="33bap3fn531l0" /> : activeSubTab === 'configurer' ? templateEditor.open ? <NoticeComposer key={templateEditor.template?.id ?? 'new-template'} templateMode editingTemplate={templateEditor.template} legacyMode={false} onToggleLegacy={() => undefined} contentLang={contentLang} onContentLangChange={setContentLang} templates={templates} onSaveTemplate={saveTemplate} onSubmit={closeTemplateEditor} data-id="element-1811" data-mp="ihnpydo4gi6u" /> : <CommunicationsConfigurer categories={categories} onCategoriesChange={setCategories} templates={templates} initialSection={configSection} onCreateTemplate={() => openTemplateEditor(null)} onEditTemplate={openTemplateEditor} onDeleteTemplate={id => setTemplates(prev => prev.filter(tpl => tpl.id !== id))} data-id="element-1812" data-mp="36b13xg73l10u" /> : gererView === 'list' ? <NoticeList notices={notices} onCreate={() => openComposer(null)} onEdit={openComposer} onViewStats={viewStats} data-id="element-1813" data-mp="2ilggol3wvhlr" /> : legacyMode ? <LegacyNoticeComposer legacyMode={legacyMode} onToggleLegacy={setLegacyMode} data-id="element-1814" data-mp="12tmcwi5srn92" /> : <NoticeComposer legacyMode={legacyMode} onToggleLegacy={setLegacyMode} contentLang={contentLang} onContentLangChange={setContentLang} editingNotice={editingNotice} templates={templates} onSaveTemplate={saveTemplate} onSubmit={backToList} data-id="element-1815" data-mp="o61bqtshfy3s" />}
    </div>;
}